<template>
  <div class="space-y-4">
    <!-- Soil Data Overview -->
    <div class="glass-card p-4">
      <div class="flex justify-between items-center mb-4">
        <h3 class="font-bold flex items-center gap-2">
          <div class="w-8 h-8 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
            </svg>
          </div>
          Soil Health
        </h3>
        <button class="text-xs themed-text-secondary hover:underline">View Details</button>
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div class="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 p-3 rounded-xl">
          <div class="flex justify-between items-start mb-2">
            <span class="text-2xl">🌱</span>
            <span class="text-xs bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300 px-2 py-1 rounded-full">Good</span>
          </div>
          <p class="font-bold text-lg">6.8</p>
          <p class="text-xs themed-text-secondary">Average pH</p>
          <div class="mt-2 h-1 bg-emerald-200 dark:bg-emerald-800 rounded-full overflow-hidden">
            <div class="h-full bg-emerald-500 rounded-full" style="width: 68%"></div>
          </div>
        </div>

        <div class="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-3 rounded-xl">
          <div class="flex justify-between items-start mb-2">
            <span class="text-2xl">💧</span>
            <span class="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 px-2 py-1 rounded-full">Optimal</span>
          </div>
          <p class="font-bold text-lg">68%</p>
          <p class="text-xs themed-text-secondary">Moisture</p>
          <div class="mt-2 h-1 bg-blue-200 dark:bg-blue-800 rounded-full overflow-hidden">
            <div class="h-full bg-blue-500 rounded-full" style="width: 68%"></div>
          </div>
        </div>

        <div class="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 p-3 rounded-xl">
          <div class="flex justify-between items-start mb-2">
            <span class="text-2xl">🌡️</span>
            <span class="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300 px-2 py-1 rounded-full">Normal</span>
          </div>
          <p class="font-bold text-lg">24°C</p>
          <p class="text-xs themed-text-secondary">Soil Temp</p>
          <div class="mt-2 h-1 bg-amber-200 dark:bg-amber-800 rounded-full overflow-hidden">
            <div class="h-full bg-amber-500 rounded-full" style="width: 60%"></div>
          </div>
        </div>

        <div class="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-3 rounded-xl">
          <div class="flex justify-between items-start mb-2">
            <span class="text-2xl">🧪</span>
            <span class="text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300 px-2 py-1 rounded-full">High</span>
          </div>
          <p class="font-bold text-lg">85%</p>
          <p class="text-xs themed-text-secondary">Nitrogen</p>
          <div class="mt-2 h-1 bg-purple-200 dark:bg-purple-800 rounded-full overflow-hidden">
            <div class="h-full bg-purple-500 rounded-full" style="width: 85%"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Drone Fleet Status -->
    <div class="glass-card p-4">
      <div class="flex justify-between items-center mb-4">
        <h3 class="font-bold flex items-center gap-2">
          <div class="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
            <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
            </svg>
          </div>
          Drone Fleet
        </h3>
        <button class="text-xs themed-text-secondary hover:underline">Manage</button>
      </div>

      <div class="space-y-3">
        <div v-for="drone in drones" :key="drone.id" 
             class="flex items-center justify-between p-3 rounded-xl bg-white/5 dark:bg-black/5 hover:bg-white/10 dark:hover:bg-black/10 transition-colors cursor-pointer">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                 :class="droneStatusClass(drone.status)">
              {{ drone.id }}
            </div>
            <div>
              <p class="font-medium">{{ drone.name }}</p>
              <p class="text-xs themed-text-secondary">{{ drone.model }}</p>
            </div>
          </div>
          <div class="text-right">
            <div class="flex items-center gap-1">
              <div :class="statusDotClass(drone.status)" class="w-2 h-2 rounded-full"></div>
              <span class="text-xs font-medium">{{ drone.status }}</span>
            </div>
            <p class="text-xs themed-text-secondary">{{ drone.battery }}% battery</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="glass-card p-4">
      <h3 class="font-bold mb-4">Quick Actions</h3>
      <div class="grid grid-cols-2 gap-3">
        <button class="p-3 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg transition-all hover-lift">
          <span class="text-xl mb-1">🚁</span>
          <span class="text-sm font-medium">Deploy Drone</span>
        </button>
        <button class="p-3 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:shadow-lg transition-all hover-lift">
          <span class="text-xl mb-1">📊</span>
          <span class="text-sm font-medium">Run Analysis</span>
        </button>
        <button class="p-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 text-white hover:shadow-lg transition-all hover-lift">
          <span class="text-xl mb-1">💧</span>
          <span class="text-sm font-medium">Schedule Irrigation</span>
        </button>
        <button class="p-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 text-white hover:shadow-lg transition-all hover-lift">
          <span class="text-xl mb-1">📈</span>
          <span class="text-sm font-medium">View Reports</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const drones = ref([
  { id: 'D1', name: 'Alpha One', model: 'DJI Agras T30', status: 'active', battery: 87 },
  { id: 'D2', name: 'Beta Two', model: 'DJI Agras T40', status: 'monitoring', battery: 92 },
  { id: 'D3', name: 'Gamma Three', model: 'DJI Agras T30', status: 'idle', battery: 45 }
])

const droneStatusClass = (status) => {
  const classes = {
    active: 'bg-gradient-to-br from-emerald-500 to-green-600',
    monitoring: 'bg-gradient-to-br from-blue-500 to-indigo-600',
    idle: 'bg-gradient-to-br from-gray-500 to-gray-600',
    maintenance: 'bg-gradient-to-br from-red-500 to-pink-600'
  }
  return classes[status] || classes.idle
}

const statusDotClass = (status) => {
  const classes = {
    active: 'bg-emerald-500',
    monitoring: 'bg-blue-500',
    idle: 'bg-gray-500',
    maintenance: 'bg-red-500'
  }
  return classes[status] || classes.idle
}
</script>
